import { z } from 'zod';
import {
  FINANCIAL_LIMITS,
  validateAmount,
  validateExpenseDate,
  validateGSTAmount,
  validateQuantity,
  VALIDATION_PATTERNS
} from '../../utils/enterpriseValidation';

// ============================================================================
// ENTERPRISE-GRADE ZOD SCHEMA
// ============================================================================

// Custom Zod validators using enterprise validation
const amountValidator = (fieldName: string) => z.string()
  .refine((val) => {
    if (!val || val.trim() === '') return false;
    const result = validateAmount(val, fieldName);
    return result.isValid;
  }, (val) => {
    const result = validateAmount(val || '', fieldName);
    return { message: result.errors[0] || `Invalid ${fieldName.toLowerCase()}` };
  });

const optionalAmountValidator = (fieldName: string) => z.string()
  .optional()
  .refine((val) => {
    if (!val || val.trim() === '') return true; // Optional field
    const result = validateAmount(val, fieldName);
    return result.isValid;
  }, (val) => {
    const result = validateAmount(val || '', fieldName);
    return { message: result.errors[0] || `Invalid ${fieldName.toLowerCase()}` };
  });

const quantityValidator = z.string()
  .refine((val) => {
    if (!val || val.trim() === '') return false;
    const result = validateQuantity(val);
    return result.isValid;
  }, (val) => {
    const result = validateQuantity(val || '');
    return { message: result.errors[0] || 'Invalid quantity' };
  });

const dateValidator = z.string()
  .refine((val) => {
    if (!val || val.trim() === '') return false;
    const result = validateExpenseDate(val);
    return result.isValid;
  }, (val) => {
    const result = validateExpenseDate(val || '');
    return { message: result.errors[0] || 'Invalid date' };
  });

// Text field validators with security
const secureTextValidator = (maxLength: number, fieldName: string) => z.string()
  .max(maxLength, `${fieldName} cannot exceed ${maxLength} characters`)
  .refine((val) => {
    if (!val) return true;
    // Check for dangerous patterns
    const dangerousPatterns = [/<script|javascript:|on\w+\s*=|expression\s*\(|vbscript:/i];
    return !dangerousPatterns.some(pattern => pattern.test(val));
  }, `${fieldName} contains potentially dangerous content`)
  .refine((val) => {
    if (!val) return true;
    // Check for forbidden characters in text
    const forbiddenInText = /[<>\"'`;&|\\]/;
    return !forbiddenInText.test(val);
  }, `${fieldName} contains invalid characters`);

// Enhanced Zod schema with enterprise-grade validation
export const zodExpenseSchema = z.object({
  // ========================================================================
  // REQUIRED FIELDS - STRICT VALIDATION
  // ========================================================================
  type: z.string()
    .min(1, 'Business type is required')
    .max(50, 'Business type name too long'),

  unit: z.string()
    .min(1, 'Unit is required')
    .max(50, 'Unit name too long'),

  costCenter: z.string()
    .min(1, 'Cost center is required')
    .max(50, 'Cost center name too long'),

  category: z.string()
    .min(1, 'Expense category is required')
    .max(50, 'Category name too long'),

  date: dateValidator,

  amount: amountValidator('Total Amount'),

  // ========================================================================
  // AMOUNT CALCULATION FIELDS
  // ========================================================================
  mode: z.enum(['total', 'unit'], {
    errorMap: () => ({ message: 'Calculation mode must be either "total" or "unit"' })
  }).optional().default('total'),

  gross: optionalAmountValidator('Gross Amount'),
  quantity: z.string().optional(),
  rate: optionalAmountValidator('Rate'),

  // ========================================================================
  // TAX/GST FIELDS WITH BUSINESS LOGIC
  // ========================================================================
  enableGst: z.boolean().optional().default(false),
  gstAmount: optionalAmountValidator('GST Amount'),
  taxAmount: optionalAmountValidator('Tax Amount'), // Legacy support

  // ========================================================================
  // PARTY & DESCRIPTION FIELDS - SECURITY FOCUSED
  // ========================================================================
  // Deprecated: kept for backwards compatibility
  shop: z.string()
    .max(FINANCIAL_LIMITS.MAX_PARTY_NAME_LENGTH, `Shop/Party Name cannot exceed ${FINANCIAL_LIMITS.MAX_PARTY_NAME_LENGTH} characters`)
    .refine((val) => {
      if (!val) return true;
      // Allow alphanumeric, spaces, and common business punctuation: & ( ) . - , '
      return VALIDATION_PATTERNS.PARTY_NAME.test(val);
    }, 'Shop/Party Name contains invalid characters')
    .refine((val) => {
      if (!val) return true;
      // Check for dangerous patterns but allow & and parentheses
      const dangerousPatterns = [/<script|javascript:|on\w+\s*=|expression\s*\(|vbscript:/i];
      return !dangerousPatterns.some(pattern => pattern.test(val));
    }, 'Shop/Party Name contains potentially dangerous content')
    .refine((val) => {
      if (!val) return true;
      // Only forbid truly dangerous characters (allow & and parentheses)
      const forbiddenInText = /[<>\"`;|\\]/;
      return !forbiddenInText.test(val);
    }, 'Shop/Party Name contains invalid characters')
    .optional(),

  // New unified party system fields
  partyName: z.string()
    .max(FINANCIAL_LIMITS.MAX_PARTY_NAME_LENGTH, `Party Name cannot exceed ${FINANCIAL_LIMITS.MAX_PARTY_NAME_LENGTH} characters`)
    .refine((val) => {
      if (!val) return true;
      return VALIDATION_PATTERNS.PARTY_NAME.test(val);
    }, 'Party Name contains invalid characters')
    .refine((val) => {
      if (!val) return true;
      const dangerousPatterns = [/<script|javascript:|on\w+\s*=|expression\s*\(|vbscript:/i];
      return !dangerousPatterns.some(pattern => pattern.test(val));
    }, 'Party Name contains potentially dangerous content')
    .refine((val) => {
      if (!val) return true;
      const forbiddenInText = /[<>\"`;|\\]/;
      return !forbiddenInText.test(val);
    }, 'Party Name contains invalid characters')
    .optional(),

  partyType: z.enum(['shop', 'vendor', 'office', 'labourGroup'], {
    errorMap: () => ({ message: 'Party type must be one of: shop, vendor, office, labourGroup' })
  }).optional(),

  partyId: z.string()
    .max(100, 'Party ID too long')
    .optional(),

  description: secureTextValidator(FINANCIAL_LIMITS.MAX_DESCRIPTION_LENGTH, 'Description').optional(),
  name: secureTextValidator(100, 'Item Name').optional(),

  // ========================================================================
  // DATE FIELDS WITH BUSINESS RULES
  // ========================================================================
  dateFrom: z.string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      return VALIDATION_PATTERNS.DATE_ISO.test(val);
    }, 'From date must be in valid format (YYYY-MM-DD)'),

  dateTo: z.string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      return VALIDATION_PATTERNS.DATE_ISO.test(val);
    }, 'To date must be in valid format (YYYY-MM-DD)'),

  dueDate: z.string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      return VALIDATION_PATTERNS.DATE_ISO.test(val);
    }, 'Due date must be in valid format (YYYY-MM-DD)'),

  // ========================================================================
  // BOOLEAN FLAGS
  // ========================================================================
  showName: z.boolean().optional().default(false),
  recurring: z.boolean().optional().default(false),
  isConsumable: z.boolean().optional().default(false),

  // ========================================================================
  // MODULE-SPECIFIC FIELDS
  // ========================================================================
  approvalStatus: z.enum(['pending', 'approved', 'rejected']).optional(),
  approvalComments: secureTextValidator(500, 'Approval Comments').optional(),
  itemName: secureTextValidator(100, 'Item Name').optional(),
  stockItem: z.string().optional(), // For predefined item selection in stock categories
  unitsSpent: optionalAmountValidator('Units Spent'),
  balance: optionalAmountValidator('Balance'),
  payoutMode: z.union([
    z.enum(['time', 'output', 'monthly', 'fixed']),
    z.literal('')
  ]).optional().transform(val => val === '' ? undefined : val),
  payoutFrequency: z.union([
    z.enum(['daily', 'weekly', 'monthly', 'quarterly']),
    z.literal('')
  ]).optional().transform(val => val === '' ? undefined : val),
  invoiceNo: z.string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      return VALIDATION_PATTERNS.INVOICE_NO.test(val);
    }, 'Invoice number contains invalid characters'),
  nature: z.enum(['routine', 'stock', 'capital', 'payroll', 'admin-finance', 'maintenance']).optional(),
  attachment: z.any().optional(),
})
  .superRefine((val, ctx) => {
    // ========================================================================
    // CROSS-FIELD VALIDATION & BUSINESS RULES
    // ========================================================================

    // Mode-specific validation
    if (val.mode === 'unit') {
      if (!val.quantity || val.quantity.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Quantity is required when using unit-based calculation',
          path: ['quantity']
        });
      } else {
        const quantityResult = validateQuantity(val.quantity);
        if (!quantityResult.isValid) {
          ctx.addIssue({
            code: 'custom',
            message: quantityResult.errors[0],
            path: ['quantity']
          });
        }
      }

      if (!val.rate || val.rate.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Rate is required when using unit-based calculation',
          path: ['rate']
        });
      }
    }

    if (val.mode === 'total') {
      if (!val.gross || val.gross.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'Gross amount is required when using total-based calculation',
          path: ['gross']
        });
      }
    }

    // GST validation with business logic - only validate when GST is enabled
    if (val.enableGst === true) {
      // If GST is enabled, gstAmount should be present
      if (!val.gstAmount || val.gstAmount.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'GST amount is required when GST is enabled',
          path: ['gstAmount']
        });
      } else if (val.gross) {
        // Validate GST amount against gross amount
        const gstResult = validateGSTAmount(val.gstAmount, val.gross);
        if (!gstResult.isValid) {
          ctx.addIssue({
            code: 'custom',
            message: gstResult.errors[0],
            path: ['gstAmount']
          });
        }
      }
    }

    // Name field requirement
    if (val.showName && (!val.name || val.name.trim() === '')) {
      ctx.addIssue({
        code: 'custom',
        message: 'Item name is required when name field is enabled',
        path: ['name']
      });
    }

    // Recurring expense validation
    if (val.recurring && (!val.dueDate || val.dueDate.trim() === '')) {
      ctx.addIssue({
        code: 'custom',
        message: 'Due date is required for recurring expenses',
        path: ['dueDate']
      });
    }

    // Date range validation
    if (val.dateFrom && val.dateTo) {
      const fromDate = new Date(val.dateFrom);
      const toDate = new Date(val.dateTo);
      if (fromDate > toDate) {
        ctx.addIssue({
          code: 'custom',
          message: 'From date cannot be later than To date',
          path: ['dateFrom']
        });
      }
    }

    // Amount consistency check
    if (val.mode === 'unit' && val.quantity && val.rate) {
      const quantity = parseFloat(val.quantity);
      const rate = parseFloat(val.rate);
      const calculatedGross = quantity * rate;

      if (val.gross) {
        const grossAmount = parseFloat(val.gross);
        const difference = Math.abs(grossAmount - calculatedGross);

        // Allow small rounding differences (1 paisa)
        if (difference > 0.01) {
          ctx.addIssue({
            code: 'custom',
            message: `Gross amount (₹${grossAmount}) does not match calculated amount (₹${calculatedGross.toFixed(2)})`,
            path: ['gross']
          });
        }
      }
    }

    // Capital goods specific validation
    if (val.nature === 'capital' && val.unitsSpent && val.quantity) {
      const spent = parseFloat(val.unitsSpent);
      const total = parseFloat(val.quantity);

      if (spent > total) {
        ctx.addIssue({
          code: 'custom',
          message: 'Units spent cannot exceed total quantity purchased',
          path: ['unitsSpent']
        });
      }
    }

    // Unified party system validation
    if (val.partyName && !val.partyType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Party type is required when party name is provided',
        path: ['partyType']
      });
    }

    if (val.partyType && !val.partyName) {
      ctx.addIssue({
        code: 'custom',
        message: 'Party name is required when party type is provided',
        path: ['partyName']
      });
    }

    // Validate party type matches nature
    if (val.nature && val.partyType) {
      const natureToPartyType: Record<string, string> = {
        'routine': 'shop',
        'stock': 'vendor',
        'capital': 'vendor',
        'payroll': 'labourGroup',
        'admin-finance': 'office',
        'maintenance': 'vendor'
      };

      const expectedPartyType = natureToPartyType[val.nature];
      if (expectedPartyType && val.partyType !== expectedPartyType) {
        ctx.addIssue({
          code: 'custom',
          message: `For ${val.nature} expenses, party type must be ${expectedPartyType}`,
          path: ['partyType']
        });
      }
    }

  })

export type ExpenseFormValues = z.infer<typeof zodExpenseSchema>
